<template>
  <div class="oc-step-review">
    <div class="oc-step-header oc-step-header--compact mb-50">
      <div class="oc-step-header__icon oc-step-header__icon--compact">
        <feather-icon icon="CheckSquareIcon" size="16" />
      </div>
      <div class="oc-step-header__body">
        <span class="oc-step-header__title">Review &amp; save</span>
        <span class="oc-step-header__hint">
          · Check summary — live preview is on the left
        </span>
      </div>
    </div>

    <b-alert
      v-if="excludedLetterSectionsLabel"
      show
      variant="light"
      class="py-75 mb-1 oc-excluded-alert"
    >
      <feather-icon icon="EyeOffIcon" size="14" class="mr-50 flex-shrink-0 text-muted" />
      <span class="small">
        Hidden from preview &amp; PDF:
        <strong>{{ excludedLetterSectionsLabel }}</strong>.
        Turn them back on in Write Letter if needed.
      </span>
    </b-alert>

    <div class="oc-review-summary">
      <div class="oc-review-summary__head">
        <feather-icon icon="ClipboardIcon" size="16" class="mr-50" />
        Summary
      </div>

      <div class="oc-summary-group">
        <div class="oc-summary-group__title">Site &amp; role</div>
        <div class="oc-summary-row">
          <span class="oc-summary-row__label">Site Name</span>
          <strong class="oc-summary-row__value">{{ form.site_name || '—' }}</strong>
        </div>
        <div class="oc-summary-row">
          <span class="oc-summary-row__label">Site code</span>
          <code class="oc-summary-row__value">{{ form.site_code || '—' }}</code>
        </div>
        <div class="oc-summary-row">
          <span class="oc-summary-row__label">Client</span>
          <strong class="oc-summary-row__value">{{ form.client || '—' }}</strong>
        </div>
        <div class="oc-summary-row">
          <span class="oc-summary-row__label">Job title</span>
          <strong class="oc-summary-row__value">{{ form.jobTitle || '—' }}</strong>
        </div>
        <div class="oc-summary-row">
          <span class="oc-summary-row__label">Location</span>
          <strong class="oc-summary-row__value">{{ form.location || '—' }}</strong>
        </div>
        <div class="oc-summary-row">
          <span class="oc-summary-row__label">Status</span>
          <b-badge :variant="form.is_active ? 'light-success' : 'light-secondary'">
            {{ form.is_active ? 'Active' : 'Inactive' }}
          </b-badge>
        </div>
      </div>

      <div class="oc-summary-group">
        <div class="oc-summary-group__title">Letter content</div>
        <div class="oc-summary-row">
          <span class="oc-summary-row__label">Title</span>
          <strong class="oc-summary-row__value">{{ letterTitleText || 'OFFER LETTER' }}</strong>
        </div>
        <div class="oc-summary-row">
          <span class="oc-summary-row__label">Stamp</span>
          <strong class="oc-summary-row__value">{{ signatureSummaryLabel }}</strong>
        </div>
        <div class="oc-summary-row oc-summary-row--stack">
          <span class="oc-summary-row__label">Sections</span>
          <strong class="oc-summary-row__value">{{ includedLetterSectionsLabel }}</strong>
        </div>
      </div>

      <div class="oc-summary-group mb-0">
        <div class="oc-summary-group__title">Extras</div>
        <div class="oc-summary-row">
          <span class="oc-summary-row__label">Salary rows</span>
          <strong class="oc-summary-row__value">{{ salaryRowCount }}</strong>
        </div>
        <div class="oc-summary-row">
          <span class="oc-summary-row__label">Branding</span>
          <strong class="oc-summary-row__value">{{ uploadedImageCount }} of {{ imageKeys.length }} images</strong>
        </div>
      </div>

      <b-alert
        v-if="!isFormValid"
        show
        variant="warning"
        class="py-75 mt-1 mb-75"
      >
        <feather-icon icon="AlertCircleIcon" size="14" class="mr-50" />
        <small>Site code, site name, and job title are required before saving.</small>
      </b-alert>

      <b-button
        v-if="canManage"
        variant="outline-primary"
        block
        class="oc-touch-btn mb-50"
        :disabled="previewing || !hasPreviewContent"
        @click="$emit('preview-pdf')"
      >
        <b-spinner v-if="previewing" small class="mr-50" />
        <feather-icon v-else icon="FileIcon" size="15" class="mr-50" />
        Preview PDF
      </b-button>
      <small class="text-muted d-block text-center pb-1">
        Same sample HTML as the live preview on the left.
      </small>
    </div>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BSpinner,
} from 'bootstrap-vue'

export default {
  name: 'OfferConfigureStepReview',
  components: {
    BAlert,
    BBadge,
    BButton,
    BSpinner,
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
    canManage: {
      type: Boolean,
      default: false,
    },
    isFormValid: {
      type: Boolean,
      default: false,
    },
    previewing: {
      type: Boolean,
      default: false,
    },
    hasPreviewContent: {
      type: Boolean,
      default: false,
    },
    excludedLetterSectionsLabel: {
      type: String,
      default: '',
    },
    letterTitleText: {
      type: String,
      default: '',
    },
    signatureSummaryLabel: {
      type: String,
      default: '',
    },
    includedLetterSectionsLabel: {
      type: String,
      default: '',
    },
    salaryRowCount: {
      type: Number,
      default: 0,
    },
    uploadedImageCount: {
      type: Number,
      default: 0,
    },
    imageKeys: {
      type: Array,
      required: true,
    },
  },
}
</script>

<style scoped>
.oc-excluded-alert {
  display: flex;
  align-items: flex-start;
  border: 1px solid #eef0f3;
}

.oc-review-summary {
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 0.5rem;
  overflow: hidden;
}

.oc-review-summary__head {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f8f9fb;
  border-bottom: 1px solid #eef0f3;
  font-size: 0.875rem;
  font-weight: 700;
}

.oc-summary-group {
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #f3f4f6;
}

.oc-summary-group__title {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6e6b7b;
  margin-bottom: 0.5rem;
}

.oc-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.3rem 0;
  font-size: 0.8125rem;
}

.oc-summary-row--stack {
  flex-direction: column;
  align-items: flex-start;
}

.oc-summary-row__label {
  color: #6e6b7b;
  flex-shrink: 0;
}

.oc-summary-row__value {
  text-align: right;
  word-break: break-word;
}

.oc-summary-row--stack .oc-summary-row__value {
  text-align: left;
}

.oc-review-summary .oc-touch-btn,
.oc-review-summary .b-alert,
.oc-review-summary > small {
  margin-left: 1rem;
  margin-right: 1rem;
}

.oc-review-summary > .oc-touch-btn {
  margin-top: 0.5rem;
}

.oc-touch-btn {
  min-height: 2.375rem;
}
</style>
